import React, { forwardRef, useRef } from 'react';
import { Cropper, ReactCropperElement } from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import { CROPPER_CONTAINER_HEIGHT, CROPPER_CONTAINER_WIDTH } from '../../constants/service/common/utilConstants';
import Button from '../Button';
import { ViewModalImageType } from '../../types/common/ImageDataType';

interface ReactCropperProps {
  rotatable: boolean;
  uploadCroppedFile: (obj: File) => void;
  files: File | null;
  openImgViewModal: (data: ViewModalImageType) => void;
}

interface ReactCropperHandles extends HTMLDivElement {
  handleClickCrop: () => void;
}

const ReactCropper = forwardRef<HTMLDivElement, ReactCropperProps>((props, ref) => {
  const cropperRef = useRef<ReactCropperElement>(null);
  const { rotatable, uploadCroppedFile, files, openImgViewModal } = props;

  /**
   * 이미지 크롭하기
   */
  const handleClickCrop = () => {
    if (!cropperRef.current) {
      return;
    }
    const cropper = cropperRef?.current.cropper;

    const croppedCanvas = cropper.getCroppedCanvas();

    if (croppedCanvas === undefined) {
      return;
    }

    croppedCanvas.toBlob((blob) => {
      if (!blob || !files) {
        return;
      }
      const croppedFile = new File([blob], files?.name, { type: files?.type });
      uploadCroppedFile(croppedFile);
      cropper.reset();
    });
  };
  /**
   *  이미지 회전
   */
  const handleRotate = () => {
    if (!cropperRef.current) {
      return;
    }
    const cropper = cropperRef?.current.cropper;
    cropper?.rotate(90);
  };

  const openPreviewCroppedImg = () => {
    if (!cropperRef.current) {
      return;
    }
    /**
     *  크롭된 이미지 만들기
     */
    const previewImg = cropperRef?.current.cropper
      .getCroppedCanvas({
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      })
      .toDataURL('image/jpeg');
    /**
     *  미리보기 모달 띄우기
     */
    openImgViewModal({
      url: previewImg,
    });
  };

  React.useImperativeHandle(
    ref,
    () =>
      ({
        handleClickCrop,
      }) as ReactCropperHandles,
  );

  return (
    <div className="img-cropper-modal">
      <Cropper
        className="cropperWrap"
        style={{
          height: CROPPER_CONTAINER_HEIGHT,
          width: CROPPER_CONTAINER_WIDTH,
        }}
        viewMode={1}
        minCropBoxWidth={300}
        minCropBoxHeight={300}
        aspectRatio={1}
        guides={true}
        center={true}
        src={files ? URL.createObjectURL(files) : undefined}
        ref={cropperRef}
        cropBoxResizable={false}
        toggleDragModeOnDblclick={false}
        dragMode="move"
        onClick={handleClickCrop}
      />
      {rotatable ? <Button className="rotate-btn" color="light" onClick={handleRotate} text="회전" /> : null}
      <Button className="preview-btn" color="primary" onClick={openPreviewCroppedImg} text="미리보기" />
      <div className="notify">
        <div>*크롭 기능을 사용 시, 이미지 품질의 저하가 있을 수 있으므로, 자체 수정이 가능한 경우,</div>
        <div>800X800 사이즈 이상, 1:1 비율의 이미지를 올리시는 것을 권장합니다.</div>
      </div>
    </div>
  );
});

ReactCropper.displayName = 'ReactCropper';

export default ReactCropper;
